<template>
    <AppHeader />
    <main class="flex-1 p-4 max-w-2xl mx-auto w-full">
        <RouterView :key="$route.fullPath" />
    </main>
    <AppSnackbar v-model="network.networkError" variant="error">
        {{ t('app.networkError') }}
    </AppSnackbar>
    <VerifyDialog />
</template>

<script setup lang="ts">
    import { onBeforeUnmount, onMounted } from 'vue'
    import AppHeader from '@/components/AppHeader.vue'
    import AppSnackbar from '@/components/ui/AppSnackbar.vue'
    import VerifyDialog from '@/components/VerifyDialog.vue'
    import { useAuthStore } from '@/stores/auth'
    import { useQueueStore } from '@/stores/queue'
    import { useNetworkStore } from '@/stores/network'
    import { useI18n } from 'vue-i18n'
    import { useApplyConfig } from '@/composables/useApplyConfig'

    const auth = useAuthStore()
    const queue = useQueueStore()
    const network = useNetworkStore()
    const { t } = useI18n()
    const { apply } = useApplyConfig()

    let queueTimer: number | undefined
    const onlineHandler = () => void queue.flushQueue()

    onMounted(async () => {
        try {
            await auth.getUserStatus()
        } catch {
            // ignore — user just isn't logged in
        }
        // Deep-link: opened from a QR with ?cmd=<urlencoded payload>. Apply the
        // command, then strip ?cmd= from the URL so a reload doesn't re-run it.
        // apply() navigates to scanner/home on success.
        const params = new URLSearchParams(window.location.search)
        const cmd = params.get('cmd')
        if (cmd) {
            // Clear the query first so apply()'s navigation doesn't leave ?cmd=
            // dangling and a later reload is a clean start.
            window.history.replaceState({}, '', window.location.pathname + window.location.hash)
            try {
                await apply(decodeURIComponent(cmd))
            } catch {
                // A failed deep-link (e.g. network error mid-login) must not block
                // the rest of app startup (queue timer, online listener).
            }
        }
        queueTimer = window.setInterval(() => void queue.flushQueue(), 5000)
        window.addEventListener('online', onlineHandler)
    })

    onBeforeUnmount(() => {
        if (queueTimer) window.clearInterval(queueTimer)
        window.removeEventListener('online', onlineHandler)
    })
</script>
